import { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useBlogs } from '@/hooks/useBlogs';
import { toast } from 'sonner';
import { TagInput } from '@/components/ui/tag-input';
import { MarkdownEditor } from '@/components/blog/MarkdownEditor';
import { normalizeMarkdown } from '@/lib/markdown-utils';

interface BlogEditorProps {
  blog?: any;
  onSave: () => void;
  onCancel: () => void;
}

// Trigger TS refresh
export function BlogEditor({ blog, onSave, onCancel }: BlogEditorProps) {
  const { createBlog, updateBlog } = useBlogs();
  const isEditing = !!blog;

  const [formData, setFormData] = useState({
    title: blog?.title || '',
    slug: blog?.slug || '',
    excerpt: blog?.excerpt || '',
    content: blog?.content || '',
    author: blog?.author || 'NexQR Team',
    published: blog?.published || false,
    featured: blog?.featured || false,
    read_time: blog?.read_time || 5,
    tags: blog?.tags || [],
    seo_title: blog?.seo_title || '',
    seo_description: blog?.seo_description || '',
    image_url: blog?.image_url || '',
  });

  useEffect(() => {
    if (blog) {
      setFormData({
        title: blog.title || '',
        slug: blog.slug || '',
        excerpt: blog.excerpt || '',
        content: blog.content || '',
        author: blog.author || 'NexQR Team',
        published: blog.published || false,
        featured: blog.featured || false,
        read_time: blog.read_time || 5,
        tags: blog.tags || [],
        seo_title: blog.seo_title || '',
        seo_description: blog.seo_description || '',
        image_url: blog.image_url || '',
      });
    }
  }, [blog]);

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleTagChange = (tags: string[]) => {
    setFormData(prev => ({
      ...prev,
      tags
    }));
  };

  const handleSubmit = async () => {
    try {
      if (isEditing) {
        await updateBlog.mutateAsync({
          id: blog.id,
          ...formData,
          tags: formData.tags,
          published_at: formData.published ? new Date().toISOString() : null,
        });
        toast.success('Blog post updated successfully');
      } else {
        await createBlog.mutateAsync({
          ...formData,
          tags: formData.tags,
          published_at: formData.published ? new Date().toISOString() : null,
        });
        toast.success('Blog post created successfully');
      }
      onSave();
    } catch (error: any) {
      toast.error(error.message || 'Failed to save blog post');
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div>
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => {
                const value = e.target.value;
                handleChange('title', value);
                if (!isEditing) {
                  handleChange('slug', value.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]+/g, ''));
                }
              }}
            />
          </div>

          <div>
            <Label htmlFor="slug">Slug</Label>
            <Input
              id="slug"
              value={formData.slug}
              onChange={(e) => handleChange('slug', e.target.value)}
              disabled={isEditing}
            />
          </div>

          <div>
            <Label htmlFor="author">Author</Label>
            <Input
              id="author"
              value={formData.author}
              onChange={(e) => handleChange('author', e.target.value)}
            />
          </div>

          <div>
            <Label htmlFor="read_time">Read Time (minutes)</Label>
            <Input
              id="read_time"
              type="number"
              value={formData.read_time}
              onChange={(e) => handleChange('read_time', parseInt(e.target.value))}
            />
          </div>

          <div>
            <Label>Tags</Label>
            <TagInput
              value={formData.tags}
              onChange={handleTagChange}
              placeholder="Enter tags..."
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Publish</Label>
              <p className="text-sm text-muted-foreground">Make this post visible to users</p>
            </div>
            <Switch
              checked={formData.published}
              onCheckedChange={(checked) => handleChange('published', checked)}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Featured</Label>
              <p className="text-sm text-muted-foreground">Show this post in featured section</p>
            </div>
            <Switch
              checked={formData.featured}
              onCheckedChange={(checked) => handleChange('featured', checked)}
            />
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <Label htmlFor="seo_title">SEO Title</Label>
            <Input
              id="seo_title"
              value={formData.seo_title}
              onChange={(e) => handleChange('seo_title', e.target.value)}
            />
          </div>

          <div>
            <Label htmlFor="seo_description">SEO Description</Label>
            <Input
              id="seo_description"
              value={formData.seo_description}
              onChange={(e) => handleChange('seo_description', e.target.value)}
            />
          </div>

          <div>
            <Label htmlFor="image_url">Featured Image URL</Label>
            <Input
              id="image_url"
              value={formData.image_url}
              onChange={(e) => handleChange('image_url', e.target.value)}
              placeholder="https://example.com/image.jpg"
            />
          </div>

          <div>
            <Label htmlFor="excerpt">Excerpt</Label>
            <Input
              id="excerpt"
              value={formData.excerpt}
              onChange={(e) => handleChange('excerpt', e.target.value)}
              placeholder="Brief description of the blog post..."
            />
          </div>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <Label>Content</Label>
          {isEditing && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                const fixed = normalizeMarkdown(formData.content);
                if (fixed !== formData.content) {
                  handleChange('content', fixed);
                  toast.success('Content formatting fixed! Review and save.');
                } else {
                  toast.info('Content formatting looks good already.');
                }
              }}
            >
              Fix Formatting
            </Button>
          )}
        </div>
        <MarkdownEditor
          value={formData.content}
          onChange={(val) => handleChange('content', val)}
        />
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="button" onClick={handleSubmit}>
          {isEditing ? 'Update' : 'Create'} Blog Post
        </Button>
      </div>
    </div>
  );
}